import { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { useTheme } from "@/context/ThemeContext";
import { PageHeader } from "@/components/common";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { PALETTES, ROLE_LABELS, subjectColor } from "@/lib/constants";
import { Save, Plus, Trash2, Moon, Sun, Upload, Image as ImageIcon, Sparkles } from "lucide-react";
import StudentOfWeek from "@/components/StudentOfWeek";

export default function Settings() {
  const { user } = useAuth();
  const { dark, setDark, palette, setPalette } = useTheme();
  const [s, setS] = useState(null);
  const [users, setUsers] = useState([]);
  const [userOpen, setUserOpen] = useState(false);
  const [newUser, setNewUser] = useState({ name: "", email: "", password: "", role: "teacher" });
  const isAdmin = user?.role === "admin";

  useEffect(() => { api.get("/settings").then((r) => setS(r.data)); if (user?.role === "admin") api.get("/auth/users").then((r) => setUsers(r.data)); /* eslint-disable-next-line */ }, []);

  const save = async () => {
    try { const { data } = await api.put("/settings", { ...s, theme_palette: palette, dark_mode: dark }); setS(data); toast.success("Ayarlar kaydedildi"); }
    catch (e) { toast.error(apiError(e)); }
  };
  const total = s?.subjects?.reduce((a, x) => a + (parseInt(x.question_count) || 0), 0) || 0;
  const setSubjCount = (i, v) => setS((p) => ({ ...p, subjects: p.subjects.map((x, idx) => idx === i ? { ...x, question_count: v } : x) }));

  const onLogo = (ev) => {
    const f = ev.target.files?.[0]; if (!f) return;
    if (!/image\/(png|jpe?g|webp)/.test(f.type)) { toast.error("Lütfen PNG, JPEG veya WEBP yükleyin"); ev.target.value = ""; return; }
    if (f.size > 5 * 1024 * 1024) { toast.error("Logo 5 MB'tan küçük olmalı"); ev.target.value = ""; return; }
    const reader = new FileReader();
    reader.onload = () => {
      const img = new Image();
      img.onload = () => {
        // Logoyu otomatik küçült: büyük görseller yükleme hatasına (413) yol açmasın
        const MAX = 256;
        let w = img.width, h = img.height;
        const scale = Math.min(1, MAX / Math.max(w, h));
        w = Math.max(1, Math.round(w * scale)); h = Math.max(1, Math.round(h * scale));
        const canvas = document.createElement("canvas");
        canvas.width = w; canvas.height = h;
        canvas.getContext("2d").drawImage(img, 0, 0, w, h);
        setS((p) => ({ ...p, logo_url: canvas.toDataURL("image/png") }));
        toast.success("Logo hazır — kaydetmeyi unutmayın");
      };
      img.onerror = () => toast.error("Görsel okunamadı, farklı bir dosya deneyin");
      img.src = String(reader.result);
    };
    reader.onerror = () => toast.error("Dosya okunamadı");
    reader.readAsDataURL(f);
    ev.target.value = "";
  };

  const addUser = async () => {
    if (!newUser.email || !newUser.password) { toast.error("E-posta ve şifre gerekli"); return; }
    try { await api.post("/auth/register", newUser); toast.success("Kullanıcı eklendi"); setUserOpen(false); setNewUser({ name: "", email: "", password: "", role: "teacher" }); api.get("/auth/users").then((r) => setUsers(r.data)); }
    catch (e) { toast.error(apiError(e)); }
  };
  const delUser = async (id) => { try { await api.delete(`/auth/users/${id}`); api.get("/auth/users").then((r) => setUsers(r.data)); toast.success("Silindi"); } catch (e) { toast.error(apiError(e)); } };

  if (!s) return <div className="text-muted-foreground">Yükleniyor...</div>;

  return (
    <div>
      <PageHeader title="Ayarlar" desc="Sistem geneli yapılandırma">
        {isAdmin && <Button onClick={save} data-testid="settings-save"><Save className="h-4 w-4 mr-2" />Kaydet</Button>}
      </PageHeader>

      <Tabs defaultValue="general">
        <TabsList className="flex-wrap h-auto">
          <TabsTrigger value="general" data-testid="tab-general">Kurum</TabsTrigger>
          <TabsTrigger value="sow" data-testid="tab-sow">Haftanın Öğrencisi</TabsTrigger>
          <TabsTrigger value="subjects" data-testid="tab-subjects">Dersler & Soru</TabsTrigger>
          <TabsTrigger value="scoring" data-testid="tab-scoring">Puanlama</TabsTrigger>
          <TabsTrigger value="appearance" data-testid="tab-appearance">Görünüm</TabsTrigger>
          {isAdmin && <TabsTrigger value="ai" data-testid="tab-ai">AI Okuma</TabsTrigger>}
          {isAdmin && <TabsTrigger value="users" data-testid="tab-users">Kullanıcılar</TabsTrigger>}
        </TabsList>

        <TabsContent value="general" className="mt-4">
          <Card className="p-6 bg-card border-border max-w-2xl space-y-4">
            <div className="grid sm:grid-cols-2 gap-4">
              <div className="space-y-1.5 sm:col-span-2"><Label>Kurum Adı</Label><Input value={s.institution_name} onChange={(e) => setS({ ...s, institution_name: e.target.value })} disabled={!isAdmin} data-testid="set-institution" /></div>
              <div className="space-y-1.5"><Label>İl / İlçe</Label><Input value={s.city || ""} onChange={(e) => setS({ ...s, city: e.target.value })} disabled={!isAdmin} /></div>
              <div className="space-y-1.5"><Label>Okul Müdürü</Label><Input value={s.principal || ""} onChange={(e) => setS({ ...s, principal: e.target.value })} disabled={!isAdmin} /></div>
              <div className="space-y-1.5"><Label>Eğitim Öğretim Yılı</Label><Input value={s.academic_year} onChange={(e) => setS({ ...s, academic_year: e.target.value })} disabled={!isAdmin} /></div>
            </div>
            <div className="pt-2 border-t border-border">
              <Label className="mb-2 block">Kurum Logosu</Label>
              <div className="flex items-center gap-4">
                <div className="h-20 w-20 rounded-lg border border-border bg-background grid place-items-center overflow-hidden shrink-0">
                  {s.logo_url ? <img src={s.logo_url} alt="logo" className="h-full w-full object-contain" data-testid="logo-preview" /> : <ImageIcon className="h-7 w-7 text-muted-foreground" />}
                </div>
                <div className="space-y-2">
                  <input type="file" accept="image/png,image/jpeg,image/webp" onChange={onLogo} disabled={!isAdmin} className="hidden" id="logo-upload" data-testid="logo-upload" />
                  <Button type="button" variant="outline" size="sm" disabled={!isAdmin} onClick={() => document.getElementById("logo-upload").click()}><Upload className="h-4 w-4 mr-2" />Logo Yükle</Button>
                  {s.logo_url && isAdmin && <Button type="button" variant="ghost" size="sm" className="text-destructive ml-2" onClick={() => setS({ ...s, logo_url: "" })} data-testid="logo-remove">Kaldır</Button>}
                  <p className="text-xs text-muted-foreground">PNG/JPG/WEBP. Yüklenen logo otomatik olarak küçültülür; her boyuttaki görsel sorunsuz yüklenir. Optik formlarda ve karnelerde görünür. Kaydet'e basmayı unutmayın.</p>
                </div>
              </div>
            </div>
          </Card>
        </TabsContent>

        <TabsContent value="sow" className="mt-4">
          <StudentOfWeek isAdmin={isAdmin} />
          {isAdmin && (
            <Card className="p-6 bg-card border-border max-w-2xl mt-5 space-y-3">
              <div className="flex items-center justify-between gap-3">
                <div>
                  <h3 className="font-semibold">Motivasyon Sözleri</h3>
                  <p className="text-xs text-muted-foreground mt-0.5">Afişin altında her hafta sırayla biri gösterilir. Boş bırakırsanız varsayılan sözler kullanılır. Değişiklikten sonra <b>Kaydet</b>'e basın.</p>
                </div>
                <Button size="sm" variant="outline" className="shrink-0" onClick={() => setS((p) => ({ ...p, sow_quotes: [...(p.sow_quotes || []), ""] }))} data-testid="quote-add"><Plus className="h-4 w-4 mr-1" />Ekle</Button>
              </div>
              <div className="space-y-2">
                {(s.sow_quotes || []).map((q, i) => (
                  <div key={i} className="flex items-center gap-2">
                    <span className="text-xs text-muted-foreground w-5 text-right">{i + 1}</span>
                    <Input value={q} onChange={(e) => setS((p) => ({ ...p, sow_quotes: (p.sow_quotes || []).map((x, idx) => idx === i ? e.target.value : x) }))} placeholder="Motivasyon sözü…" data-testid={`quote-input-${i}`} />
                    <Button variant="ghost" size="icon" className="text-destructive shrink-0" onClick={() => setS((p) => ({ ...p, sow_quotes: (p.sow_quotes || []).filter((_, idx) => idx !== i) }))} data-testid={`quote-del-${i}`}><Trash2 className="h-4 w-4" /></Button>
                  </div>
                ))}
                {(s.sow_quotes || []).length === 0 && <p className="text-xs text-muted-foreground">Henüz özel söz yok — varsayılan sözler kullanılıyor.</p>}
              </div>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="subjects" className="mt-4">
          <Card className="p-6 bg-card border-border max-w-2xl space-y-4">
            <div className="flex items-center justify-between"><h3 className="font-semibold">Varsayılan Ders Yapısı</h3><Badge className="bg-primary/10 text-primary">Toplam: {total}</Badge></div>
            <p className="text-sm text-muted-foreground">Yeni sınavlar bu yapıyı varsayılan alır. Her sınavda ayrıca değiştirilebilir.</p>
            <div className="space-y-2">
              {s.subjects.map((subj, i) => (
                <div key={subj.key} className="flex items-center gap-3 p-3 rounded-lg border border-border bg-background">
                  <span className={`px-2.5 py-1 rounded-md text-xs font-semibold ${subjectColor(subj.key).badge}`}>{subj.name}</span>
                  <Input type="number" min="0" value={subj.question_count} onChange={(e) => setSubjCount(i, e.target.value)} disabled={!isAdmin} className="w-24 ml-auto font-mono" data-testid={`set-subject-${subj.key}`} />
                  <span className="text-xs text-muted-foreground w-10">soru</span>
                </div>
              ))}
            </div>
            <div className="pt-4 border-t border-border space-y-3" data-testid="default-grade-configs">
              <div className="flex items-center gap-3">
                <Switch checked={!!s.use_default_grade_configs} onCheckedChange={(v) => setS({ ...s, use_default_grade_configs: v })} disabled={!isAdmin} data-testid="set-use-grade-defaults" />
                <div><Label className="font-semibold">Seviyeye göre varsayılan soru dağılımı</Label>
                  <p className="text-xs text-muted-foreground">Açıkken yeni sınavlar her sınıf seviyesi (5–8) için aşağıdaki dağılımı otomatik kullanır. Kapalıyken yukarıdaki tek yapı geçerlidir.</p></div>
              </div>
              {s.use_default_grade_configs && (
                <div className="grid sm:grid-cols-2 gap-3">
                  {["5", "6", "7", "8"].map((g) => {
                    const cfg = (s.default_grade_configs || {})[g] || s.subjects;
                    const tot = cfg.reduce((a, x) => a + (parseInt(x.question_count) || 0), 0);
                    return (
                      <div key={g} className="rounded-lg border border-border p-3 space-y-2" data-testid={`grade-default-${g}`}>
                        <div className="flex items-center justify-between"><span className="font-bold">{g}. Sınıf</span><Badge variant="outline">{tot} soru</Badge></div>
                        {cfg.map((subj, i) => (
                          <div key={subj.key} className="flex items-center gap-2">
                            <span className={`px-2 py-0.5 rounded text-[11px] font-semibold ${subjectColor(subj.key).badge}`}>{subj.name}</span>
                            <Input type="number" min="0" value={subj.question_count} disabled={!isAdmin} className="w-20 ml-auto h-8 font-mono" data-testid={`grade-default-${g}-${subj.key}`}
                              onChange={(e) => setS((p) => { const d = { ...(p.default_grade_configs || {}) }; d[g] = (d[g] || p.subjects).map((x, idx) => idx === i ? { ...x, question_count: parseInt(e.target.value) || 0 } : { ...x }); return { ...p, default_grade_configs: d }; })} />
                          </div>
                        ))}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </Card>
        </TabsContent>

        <TabsContent value="scoring" className="mt-4">
          <Card className="p-6 bg-card border-border max-w-2xl space-y-4">
            <div className="grid sm:grid-cols-2 gap-4">
              <div className="space-y-1.5"><Label>Yanlış Doğru Götürme Oranı</Label>
                <Input type="number" value={s.penalty_ratio} onChange={(e) => setS({ ...s, penalty_ratio: parseInt(e.target.value) || 4 })} disabled={!isAdmin} data-testid="set-penalty" />
                <p className="text-xs text-muted-foreground">{s.penalty_ratio} yanlış = 1 doğru götürür · Net = Doğru − (Yanlış / {s.penalty_ratio})</p>
              </div>
              <div className="space-y-1.5"><Label>Taban Puan (Maksimum)</Label>
                <Select value={String(s.max_score)} onValueChange={(v) => setS({ ...s, max_score: parseInt(v) })} disabled={!isAdmin}>
                  <SelectTrigger data-testid="set-maxscore"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-card border-border"><SelectItem value="100">100 üzerinden</SelectItem><SelectItem value="500">500 üzerinden (LGS)</SelectItem></SelectContent>
                </Select>
              </div>
            </div>
            <div className="flex items-center gap-2"><Switch checked={s.equal_weight} onCheckedChange={(v) => setS({ ...s, equal_weight: v })} disabled={!isAdmin} /><Label>Tüm dersler eşit ağırlıklı</Label></div>
          </Card>
        </TabsContent>

        <TabsContent value="appearance" className="mt-4">
          <Card className="p-6 bg-card border-border max-w-2xl space-y-5">
            <div>
              <Label className="mb-3 block">Kurumsal Renk Teması</Label>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                {PALETTES.map((p) => (
                  <button key={p.id} onClick={() => setPalette(p.id)} data-testid={`set-palette-${p.id}`}
                    className={`p-3 rounded-lg border-2 transition-colors ${palette === p.id ? "border-primary" : "border-border"}`}>
                    <div className="h-8 w-full rounded-md mb-2" style={{ background: p.color }} />
                    <div className="text-xs font-medium">{p.name}</div>
                  </button>
                ))}
              </div>
            </div>
            <div className="flex items-center gap-3 pt-2">
              <Button variant="outline" onClick={() => setDark(!dark)} data-testid="set-theme-toggle">{dark ? <Sun className="h-4 w-4 mr-2" /> : <Moon className="h-4 w-4 mr-2" />}{dark ? "Açık Mod" : "Koyu Mod"}</Button>
              <span className="text-sm text-muted-foreground">Mevcut: {dark ? "Koyu" : "Açık"} tema</span>
            </div>
          </Card>
        </TabsContent>

        {isAdmin && (
          <TabsContent value="ai" className="mt-4">
            <Card className="p-6 bg-card border-border max-w-2xl space-y-4">
              <div className="flex items-center gap-2">
                <Sparkles className="h-5 w-5 text-primary" />
                <h3 className="font-semibold">AI ile Optik Okuma Anahtarı</h3>
              </div>
              <p className="text-sm text-muted-foreground">Optik formların fotoğrafını yapay zeka ile okutmak için kendi OpenAI, Google Gemini veya Anthropic Claude API anahtarınızı girin. Anahtar güvenli saklanır ve ekranda tekrar gösterilmez. (Not: Formu biz ürettiğimiz için okuma öncelikle klasik optik tarama ile yapılır; AI yalnızca yedektir.)</p>
              {s.ai_api_key_set && <Badge className="bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-300">Anahtar tanımlı ✓</Badge>}
              <div className="grid sm:grid-cols-2 gap-4">
                <div className="space-y-1.5"><Label>Sağlayıcı</Label>
                  <Select value={s.ai_provider || "openai"} onValueChange={(v) => setS({ ...s, ai_provider: v })}>
                    <SelectTrigger data-testid="ai-provider"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-card border-border">
                      <SelectItem value="openai">OpenAI</SelectItem>
                      <SelectItem value="gemini">Google Gemini</SelectItem>
                      <SelectItem value="anthropic">Anthropic Claude</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5"><Label>Model</Label>
                  <Input value={s.ai_model || ""} onChange={(e) => setS({ ...s, ai_model: e.target.value })} placeholder={(s.ai_provider || "openai") === "openai" ? "gpt-5.4" : (s.ai_provider === "anthropic" ? "claude-sonnet-5-5" : "gemini-2.5-flash")} data-testid="ai-model" />
                </div>
                <div className="space-y-1.5 sm:col-span-2"><Label>API Anahtarı</Label>
                  <Input type="password" value={s.ai_api_key || ""} onChange={(e) => setS({ ...s, ai_api_key: e.target.value })} placeholder={s.ai_api_key_set ? "•••••••• (değiştirmek için yeni anahtar yazın)" : "sk-..."} data-testid="ai-key" />
                  <p className="text-xs text-muted-foreground">Boş bırakırsanız mevcut anahtar korunur. OpenAI: platform.openai.com/api-keys · Gemini: aistudio.google.com/apikey · Claude: console.anthropic.com</p>
                </div>
              </div>
            </Card>
          </TabsContent>
        )}

        {isAdmin && (
          <TabsContent value="users" className="mt-4">
            <Card className="p-6 bg-card border-border max-w-2xl">
              <div className="flex items-center justify-between mb-4"><h3 className="font-semibold">Kullanıcılar & Roller</h3><Button size="sm" onClick={() => setUserOpen(true)} data-testid="user-add"><Plus className="h-4 w-4 mr-2" />Kullanıcı Ekle</Button></div>
              <div className="divide-y divide-border">
                {users.map((u) => (
                  <div key={u.id} className="py-3 flex items-center gap-3" data-testid={`user-row-${u.email}`}>
                    <div className="h-9 w-9 rounded-full bg-primary/10 text-primary grid place-items-center font-bold">{(u.name || u.email)[0]?.toUpperCase()}</div>
                    <div className="flex-1"><div className="font-medium text-sm">{u.name}</div><div className="text-xs text-muted-foreground">{u.email}</div></div>
                    <Badge variant="secondary">{ROLE_LABELS[u.role] || u.role}</Badge>
                    {u.id !== user.id && <Button variant="ghost" size="icon" className="text-destructive" onClick={() => delUser(u.id)} data-testid={`user-del-${u.email}`}><Trash2 className="h-4 w-4" /></Button>}
                  </div>
                ))}
              </div>
            </Card>
            <Dialog open={userOpen} onOpenChange={setUserOpen}>
              <DialogContent className="bg-card border-border max-w-sm">
                <DialogHeader><DialogTitle>Yeni Kullanıcı</DialogTitle></DialogHeader>
                <div className="space-y-3">
                  <div className="space-y-1.5"><Label>Ad Soyad</Label><Input value={newUser.name} onChange={(e) => setNewUser({ ...newUser, name: e.target.value })} data-testid="nu-name" /></div>
                  <div className="space-y-1.5"><Label>E-posta</Label><Input value={newUser.email} onChange={(e) => setNewUser({ ...newUser, email: e.target.value })} data-testid="nu-email" /></div>
                  <div className="space-y-1.5"><Label>Şifre</Label><Input type="password" value={newUser.password} onChange={(e) => setNewUser({ ...newUser, password: e.target.value })} data-testid="nu-password" /></div>
                  <div className="space-y-1.5"><Label>Rol</Label>
                    <Select value={newUser.role} onValueChange={(v) => setNewUser({ ...newUser, role: v })}><SelectTrigger data-testid="nu-role"><SelectValue /></SelectTrigger>
                      <SelectContent className="bg-card border-border">{Object.entries(ROLE_LABELS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent></Select>
                  </div>
                </div>
                <DialogFooter><Button variant="outline" onClick={() => setUserOpen(false)}>Vazgeç</Button><Button onClick={addUser} data-testid="nu-save">Ekle</Button></DialogFooter>
              </DialogContent>
            </Dialog>
          </TabsContent>
        )}
      </Tabs>
    </div>
  );
}
